import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { combineLatest, concat, from, Observable, of, throwError } from 'rxjs';
import { filter, first, map, mergeMap, pluck, share, shareReplay, tap, toArray } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AdminService } from '../admin/admin.service';
import { DataToolsApps } from '../shared/DataToolsApps';
import { UtilsService } from '../shared/services/utils.service';
import { AllPrimaryEntitiesNames, BullhornFieldType, CustomObjectEntitiesTypes, Entities } from './data-rules-model';
import { IOptionItem } from './lib/autocomplete/autocomplete.component';
import { IEntityItem } from './lib/paired-autocomplete/paired-autocomplete.component';

export interface IDataSourceMeta {
  entity: Entities;
  label: string;
  isPrimary: boolean;
  meta: IFieldMeta[];
}

export interface IActionMeta {
  entity: Entities;
  label: string;
  meta: IActionFieldMeta[];
}

export interface IFieldMeta {
  entityType: Entities;
  fieldName: string;
  metaType: BullhornFieldType | 'COUNTRY';
  label: string;
  maxLength: number;
  toManyType: string | null;
  toOneType: string | null;
  addressFields?: IAddressMeta[];
  isSecondaryEntity?: boolean;
  associatedEntity?: any;
  constraints?: any;
  optionsType?: string | null;
}

export interface IAddressMeta {
  fieldName: string;
  metaType: string;
  label: string;
  maxLength: number;
}

export interface IActionFieldMeta extends IFieldMeta {
  readOnly: boolean;
  optional: boolean;
}

export interface IGetEntityOptionsParameters {
  sourceFrom: MetaSource;
  primaryOnly: boolean;
}

export class ToManyMeta {
  id: number;
  name: string;

  // TODO: add converter to different item/option classes?
}

export enum MetaSource {
  DATA_SOURCE = 'DATA_SOURCE',
  ACTION = 'ACTION'
}

@Injectable({
  providedIn: 'root'
})
export class MetaDataService {
  private static readonly flaggedEntities: { entity: string, flag: DataToolsApps }[] = [
    {entity: 'BillingProfile', flag: DataToolsApps.DATARULES_BULLHORN_ONE_ENTITIES},
  ];

  private _cache: {
    dataSourceMeta: IDataSourceMeta[],
    actionMeta: IActionMeta[]
  } = null;

  private static dataTypeMap = {
    String: BullhornFieldType.STRING,
    Timestamp: BullhornFieldType.DATE,
    Integer: BullhornFieldType.INTEGER,
    Boolean: BullhornFieldType.BOOLEAN,
    Double: BullhornFieldType.DOUBLE,
    BigDecimal: BullhornFieldType.BIG_DECIMAL,
    Address: BullhornFieldType.ADDRESS,
    Float: BullhornFieldType.FLOAT
  };

  constructor(
    private http: HttpClient,
    private utilsService: UtilsService,
    private adminService: AdminService
  ) {
  }

  private isEntityHidden(entity: string): boolean {
    const flagged = MetaDataService.flaggedEntities.find(f => f.entity === entity);
    return !!flagged && !this.adminService.checkAppEnabled(flagged.flag);
  }

  // returns void, just puts meta in the cache.
  getAllMeta(): Observable<void> {

    // the meta data is very unlikely to change during a user session
    // if necessary F5 would clear the cache
    if (this._cache)
      return of(undefined);

    console.time('getAllMeta');
    return this.http.get<any>(`${environment.metaDataUrl}/meta`).pipe(
      map(meta => {
        const dataSourceMeta: {
          entity: Entities,
          label: string,
          isPrimary: boolean, meta: IFieldMeta[]
        }[] = this.processDataSourceMeta(meta.dataSourceMeta.meta);
        const actionMeta: { entity: Entities, label: string, meta: IActionFieldMeta[] }[] = this.processActionMeta(meta.actionMeta.meta);

        return {dataSourceMeta: dataSourceMeta, actionMeta: actionMeta};
      }),
      tap(fieldMetas => this._cache = fieldMetas),
      tap(fieldMetas => console.timeEnd('getAllMeta')),
      map(fieldMetas => undefined)
    );
  }

  getEntitiesLabelsMap(sourceFrom: MetaSource = MetaSource.DATA_SOURCE): { [key: string]: string } {
    let workingArr: (IDataSourceMeta | IActionMeta)[];
    switch (sourceFrom) {
      case MetaSource.DATA_SOURCE:
        workingArr = this._cache.dataSourceMeta;
        break;
      case MetaSource.ACTION:
        workingArr = this._cache.actionMeta;
        break;
      default:
        workingArr = [];
    }
    return workingArr.reduce((accum, {entity, label}, i, arr) => {
      const result = Object.assign({}, accum);
      if (entity.includes('FileAttachment')) {
        const sourceEntity = arr.find(({entity: ent}) => {
          return entity.includes(ent) && !ent.includes('FileAttachment');
        });
        result[entity] = sourceEntity ? sourceEntity.label.trim() + ' File Attachment' : label;
      } else {
        result[entity] = label;
      }
      return result;
    }, {});
  }

  getEntitiesSelectorsMap(sourceFrom: MetaSource = MetaSource.DATA_SOURCE): { [key: string]: string } {
    let workingArr: (IDataSourceMeta | IActionMeta)[];
    switch (sourceFrom) {
      case MetaSource.DATA_SOURCE:
        workingArr = this._cache.dataSourceMeta;
        break;
      case MetaSource.ACTION:
        workingArr = this._cache.actionMeta;
        break;
      default:
        workingArr = [];
    }
    return workingArr.reduce((accum, {entity}) => {
      const result = Object.assign({}, accum);
      result[entity] = this.utilsService.parseCamelCaseToHyphenDivided(entity);
      return result;
    }, {});
  }

  getEntitiesOptions(parameters: IGetEntityOptionsParameters): IOptionItem[] {
    let workingArr: (IDataSourceMeta | IActionMeta)[];
    switch (parameters.sourceFrom) {
      case MetaSource.DATA_SOURCE:
        workingArr = this._cache.dataSourceMeta;
        break;
      case MetaSource.ACTION:
        workingArr = this._cache.actionMeta;
        break;
      default:
        workingArr = [];
    }
    return workingArr.reduce<IOptionItem[]>((accum, meta) => {
      if (this.isEntityHidden(meta.entity)) {
        return accum;
      }
      if (parameters.primaryOnly) {
        if ((meta as IDataSourceMeta).isPrimary) {
          accum.push({name: meta.entity, label: meta.label});
        }
      } else {
        accum.push({name: meta.entity, label: meta.label});
      }
      return accum;
    }, []);
  }

  getDataSourceMeta(entity: Entities, allowed?: string[]): Observable<IFieldMeta[]> {
    const metaData = this._cache.dataSourceMeta.find(em => em.entity === entity);
    if (metaData)
      return of(allowed
        ? metaData.meta.filter(field => allowed.find(fieldName => fieldName === field.fieldName))
        : metaData.meta
      );
    return throwError(() => new Error(`Cannot get datasource meta for unknown entity: ${entity}`));
  }

  getActionMeta(entity: Entities, allowed?: string[]): Observable<IActionFieldMeta[]> {
    const metaData = this._cache.actionMeta.find(em => em.entity === entity);
    if (metaData)
      return of(allowed
        ? metaData.meta.filter(field => allowed.find(fieldName => fieldName === field.fieldName))
        : metaData.meta
      );

    return throwError(() => new Error(`Cannot get action meta for unknown entity: ${entity}`));
  }

  lookUp(entityName: string, lookUpValue: string): Observable<ToManyMeta[]> {
    return this.http.get<ToManyMeta[]>(`${environment.metaDataUrl}/meta/lookUp/${entityName}`, {params: {lookUpValue}});
  }

  lookUpEntities(entityName: string, ids: number[]): Observable<ToManyMeta[]> {
    const idsString = ids.map(id => id.toString());
    return this.http.get<ToManyMeta[]>(`${environment.metaDataUrl}/meta/entity/${entityName}`, {params: {ids: idsString}});
  }

  getEntities(): Entities[] {
    const entities: Entities[] = [];
    this._cache.dataSourceMeta.forEach(data => {   // assume datasource and action will have same entities
      if (!this.isEntityHidden(data.entity)) {
        entities.push(data.entity);
      }
    });
    return entities;
  }

  filterMetaBySecondaryDescriptor(primary: Entities, secondaryDescriptor: string | null, metaSource: MetaSource = MetaSource.DATA_SOURCE)
    : Observable<IFieldMeta[] | IActionFieldMeta[]> {
    return this.getEntityNameFromSecondaryDescriptor(primary, secondaryDescriptor, metaSource)
      .pipe(
        mergeMap(({entity, allowedFields}) => metaSource === MetaSource.DATA_SOURCE
          ? this.getDataSourceMeta(entity, allowedFields)
          : this.getActionMeta(entity, allowedFields)),
        mergeMap((meta: IFieldMeta[] | IActionFieldMeta[]) => from(meta)),
        filter((f: IFieldMeta) => this.removeNotAllowedFields(primary, f, metaSource)),
        filter((f: IFieldMeta) => secondaryDescriptor ? this.checkToManyFieldsAllowed(f) : true
        ),
        toArray(),
      );
  }

  getEntityNameFromSecondaryDescriptor(primaryEntity: Entities, secondaryDescriptor: string | null, metaSource = MetaSource.DATA_SOURCE)
    : Observable<{ entity: Entities, allowedFields?: string[] }> {
    if (secondaryDescriptor) {
      const getter$ = metaSource === MetaSource.DATA_SOURCE
        ? this.getDataSourceMeta(primaryEntity).pipe(shareReplay())
        : this.getActionMeta(primaryEntity).pipe(shareReplay());
      const secondaryFieldShare$ = getter$
        .pipe(
          mergeMap(meta => from(meta)),
          first((f: IActionFieldMeta | IFieldMeta) => {
            return (f.isSecondaryEntity || (f.metaType as string) === 'ADDRESS') && f.fieldName === secondaryDescriptor;
          }),
          share()
        );

      const entityName$ = secondaryFieldShare$.pipe(
        map((f: IFieldMeta) => (f.associatedEntity ? f.associatedEntity.entity : primaryEntity) as Entities)
      );

      const allowedArray = secondaryFieldShare$.pipe(
        pluck('constraints'),
        map(constraints => constraints ? constraints.allow : null)
      );

      return combineLatest([entityName$, allowedArray])
        .pipe(
          map(([entity, allowedFields]) => ({
            entity,
            allowedFields
          }))
        );
    } else {
      return of({entity: primaryEntity});
    }
  }

  // todo works properly only with 2-layered fieldNames
  parseFieldNameToDescriptorAndFieldName(fieldName: string)
    : Observable<{ secondaryDescriptor?: string, fieldName: string }> {
    if (
      typeof fieldName === 'string'
      && fieldName.length > 0
      // tslint:disable-next-line:no-bitwise
      && ~fieldName.indexOf('.')
    ) {
      // if fieldName consist of secondary entity parameter
      const fieldNameArray = fieldName.split('.');
      return of({
        secondaryDescriptor: fieldNameArray[0],
        fieldName: fieldNameArray[fieldNameArray.length - 1]
      });
    } else {
      return of({secondaryDescriptor: null, fieldName});
    }
  }

  reduceMetaToEntitiesOptions(primary: Entities, sourceType = MetaSource.DATA_SOURCE): Observable<IEntityItem[]> {
    const meta$ = sourceType === MetaSource.DATA_SOURCE
      ? this.getDataSourceMeta(primary)
      : this.getActionMeta(primary);
    const entitiesOptions$ = meta$
      .pipe(
        mergeMap(meta => from(meta)),
        filter(item => item.isSecondaryEntity
          // todo incorrect backend meta state consistency hack
          && item.associatedEntity
        ),
        map((item: IFieldMeta): IEntityItem => {
            const entitiesOptions = {
              value: item.associatedEntity.entity,
              label: item.fieldName.includes('customObject') ? item.associatedEntity.label : item.label,
              secondaryDescriptor: item.fieldName,
              description: item.entityType
            };
            return entitiesOptions;
          }
        ),
      );

    const entityMap = this.getEntitiesLabelsMap();

    return concat(
      // primary entity as EntityOption
      of({
        value: primary,
        label: entityMap[primary] || primary,
        secondaryDescriptor: null
      } as IEntityItem),
      entitiesOptions$
    ).pipe(
      toArray()
    );
  }

  // TODO: a lot of this duplicated code could be simplified by passing transform method in as a callback.
  private processDataSourceMeta(rawMeta: any): { entity: Entities, label: string, isPrimary: boolean, meta: IFieldMeta[] }[] {
    const resultMeta: { entity: Entities, label: string, isPrimary: boolean, meta: IFieldMeta[] }[] = [];

    Object.keys(rawMeta).forEach((entityType: Entities) => {
      const entityDataSourceMeta: any = rawMeta[entityType];

      const validDataSourceFields: IFieldMeta[] = MetaDataService.dataSourceThing(entityType, entityDataSourceMeta.fields);

      const dataSourceResult: { entity: Entities, label: string, isPrimary: boolean, meta: IFieldMeta[] } = {
        entity: entityType,
        label: entityDataSourceMeta.label,
        isPrimary: entityDataSourceMeta.isPrimaryEntity,
        meta: validDataSourceFields
      };

      resultMeta.push(dataSourceResult);
    });

    return resultMeta;
  }

  private processActionMeta(rawMeta: any): { entity: Entities, label: string, meta: IActionFieldMeta[] }[] {
    const resultMeta: { entity: Entities, label: string, meta: IActionFieldMeta[] }[] = [];

    Object.keys(rawMeta).forEach((entityType: Entities) => {
      const entityMeta: any = rawMeta[entityType];

      const validFields: IActionFieldMeta[] = MetaDataService.actionThing(entityType, entityMeta.fields);

      const result: { entity: Entities, label: string, meta: IActionFieldMeta[] } = {
        entity: entityType,
        label: entityMeta.label,
        meta: validFields
      };

      resultMeta.push(result);
    });

    return resultMeta;
  }

  private static filterFields(fields): any[] {
    return fields
      .filter(f =>
        f.type === 'COMPOSITE' ? 'ADDRESS' : (f.type === 'SCALAR' || f.type === 'ID' || f.type === 'TO_MANY' || f.type === 'TO_ONE')
      );
  }

  private static dataSourceThing(entityType: Entities, fields): IFieldMeta[] {
    return MetaDataService.filterFields(fields)
      .map(field => ({entityType, ...field})).map(MetaDataService.dataSourceMetaTransform);
  }

  private static dataSourceMetaTransform(field): IFieldMeta {
    return {
      entityType: field.isSecondaryEntity ? field.associatedEntity.entity : field.entityType,
      fieldName: field.name,
      metaType: field.type === 'TO_MANY' ? 'TO_MANY' :
        (field.type === 'COMPOSITE' ? 'ADDRESS' : (field.type === 'TO_ONE' ? 'TO_ONE' : MetaDataService.dataTypeMap[field.dataType])),
      label: field.type === 'ID' ? 'ID' : field.label, maxLength: field.maxLength,
      toManyType: field.type === 'TO_MANY' ? field.associatedEntity.entity : null,
      toOneType: field.type === 'TO_ONE' ? field.associatedEntity.entity : null,
      addressFields: field.fields ? MetaDataService.addressMetaTransform(field.fields) : [],
      isSecondaryEntity: !!field.isSecondaryEntity,
      associatedEntity: field.associatedEntity ? field.associatedEntity : null,
      constraints: field.constraints ? {
        allow: field.constraints.allow
      } : null,
      optionsType: field.optionsType ?? null
    };
  }

  private static addressMetaTransform(fields: any[]): IAddressMeta[] {
    if (fields.length === 0)
      return [];
    return fields.map(f => ({
      fieldName: f.name,
      metaType: f.type,
      label: f.label,
      maxLength: f.maxLength
    }));
  }

  private static actionThing(entityType: Entities, fields): IActionFieldMeta[] {
    return MetaDataService.filterFields(fields)
      .map(field => ({entityType, ...field})).map(MetaDataService.actionMetaTransform);

  }

  private static actionMetaTransform(field): IActionFieldMeta {
    return {
      entityType: field.isSecondaryEntity ? field.associatedEntity.entity : field.entityType,
      fieldName: field.name,
      metaType: field.type === 'TO_MANY' ? 'TO_MANY' :
        (field.type === 'COMPOSITE' ? 'ADDRESS' : (field.type === 'TO_ONE' ? 'TO_ONE' : MetaDataService.dataTypeMap[field.dataType])),
      label: field.type === 'ID' ? 'ID' : field.label, maxLength: field.maxLength,
      toManyType: field.type === 'TO_MANY' ? field.associatedEntity.entity : null,
      toOneType: field.type === 'TO_ONE' ? field.associatedEntity.entity : null,
      readOnly: field.readOnly,
      optional: field.optional,
      addressFields: field.fields ? MetaDataService.addressMetaTransform(field.fields) : [],
      isSecondaryEntity: !!field.isSecondaryEntity,
      associatedEntity: field.associatedEntity,
      optionsType: field.optionsType ?? null
    };
  }

  isPrimaryEntity(entityType): boolean {
    if (this.isEntityHidden(entityType)) {
      return false;
    }
    return !!this._cache.dataSourceMeta.find(mi => mi.entity === entityType && mi.isPrimary);
  }

  isCustomObjectPrimaryEntity(entityName: AllPrimaryEntitiesNames): boolean {
    const allowedCOEntities = CustomObjectEntitiesTypes.allowedEntities();
    return this._cache.dataSourceMeta.findIndex(mi => {
        return mi.entity === entityName
          && mi.isPrimary
          && allowedCOEntities.findIndex(en => en === entityName) > -1;
      }
    ) > -1;
  }

  // for secondary entities we need to avoid 1:m associated fields except for the ones needed for the 'append entries' action
  checkToManyFieldsAllowed(f: IFieldMeta): boolean {
    if (f.metaType === 'TO_MANY') {
      switch (f.toManyType) {
        case 'Skill':
        case 'Category':
        case 'BusinessSector':
        case 'Specialty':
        case 'Tearsheet':
        case 'CorporateUser':
          return true;
        default:
          return false;
      }
    }
    return true;
  }

  removeNotAllowedFields(primary: Entities, field: IFieldMeta, metaSource: MetaSource) {
    if (primary as string === 'CandidateCertification' && metaSource === MetaSource.ACTION) {
      switch (field.fieldName) {
        case 'candidate':
        case 'modifyingUser':
        case 'certificationFileAttachments':
        case 'fileAttachments':
          return false;
      }
    }
    return true;
  }
}
